import { displayUnitSymbol } from '../constants/units'
import type { ProductPropertyType, Unit } from '../enums'

import { formatNumber } from './format-number'
import { formatPropertyValue } from './format-property-value'

/** One choice a SELECT axis offers. */
export type PropertyOption = {
  id: number
  key: string
  label: string
  sortOrder: number
}

/** The axis itself, as it is defined once for the whole app. */
export type PropertyDefinition = {
  id: number
  key: string
  label: string
  type: ProductPropertyType
  unit: Unit | null
  decimalPlaces: number
  minValue: number | null
  maxValue: number | null
  icon: string | null
  description: string | null
  sortOrder: number
  options: PropertyOption[]
}

/** How one group narrows that axis. Every nullable column is an override. */
export type GroupPropertyOverride = {
  sortOrder: number
  isRequired: boolean
  minValue: number | null
  maxValue: number | null
  icon: string | null
  description: string | null
  /** The options this group offers. Empty means all of the definition's. */
  offeredOptionIds: number[]
}

/**
 * An axis as one group presents it: the definition, with whatever that group
 * narrows applied.
 *
 * `type`, `unit` and `decimalPlaces` are the definition's and are not in the
 * override — a value lives in one column and is compared across groups, so an
 * axis declared in millimetres under one group and centimetres under another
 * would put two scales in that column.
 */
export type EffectiveProperty = Omit<PropertyDefinition, 'options'> & {
  isRequired: boolean
  options: PropertyOption[]
}

export function effectiveProperty(
  definition: PropertyDefinition,
  override: GroupPropertyOverride,
): EffectiveProperty {
  const offered = new Set(override.offeredOptionIds)
  return {
    ...definition,
    minValue: override.minValue ?? definition.minValue,
    maxValue: override.maxValue ?? definition.maxValue,
    icon: override.icon ?? definition.icon,
    description: override.description ?? definition.description,
    sortOrder: override.sortOrder,
    isRequired: override.isRequired,
    options: offered.size
      ? definition.options.filter(option => offered.has(option.id))
      : definition.options,
  }
}

/**
 * The offer to store, out of the choices a group has ticked.
 *
 * Empty means all of them — the state a freshly attached axis is in and the one
 * that needs no upkeep — so a full set collapses back to none. Beside
 * `effectiveProperty`, which is what decodes it: a convention stored in one
 * shape and read in another is one a click handler can break silently.
 */
export function offeredOptionIds(
  ticked: readonly number[],
  all: readonly PropertyOption[],
): number[] {
  return ticked.length === all.length ? [] : [...ticked]
}

/**
 * The axes of several groups, merged into the one list a datasheet renders.
 *
 * A definition two of the groups share is presented by whichever group comes
 * first in `groupIds` — the order the measure lists them in. Here rather than
 * on either side of the wire: the backend checks a write against the winner and
 * the form draws it, and the two picking differently is a figure the screen
 * calls valid and the server refuses.
 */
export function mergeEffectiveAxes<
  T extends { groupId: number; definitionId: number },
>(rows: readonly T[], groupIds: readonly number[]): T[] {
  const rank = new Map(groupIds.map((groupId, index) => [groupId, index]))
  const merged = new Map<number, T>()
  for (const row of rows) {
    const held = merged.get(row.definitionId)
    if (
      !held ||
      (rank.get(row.groupId) ?? Infinity) < (rank.get(held.groupId) ?? Infinity)
    ) {
      merged.set(row.definitionId, row)
    }
  }
  return [...merged.values()]
}

/** What an entry is on one axis. The column the type names carries it. */
export type PropertyValue = {
  definitionId: number
  numberValue: number | null
  optionId: number | null
  boolValue: boolean | null
}

/**
 * A span on one axis: a range of figures, a set of choices, or a yes/no.
 *
 * Three surfaces state one — what a group narrows its products to, what a
 * measure prints on its datasheet, what one row of its requirement matrix asks
 * a product to bring — and all three are these four columns, which is why they
 * share one control.
 *
 * The helpers below read it the way the latter two mean it: an empty column says
 * nothing. A group means the opposite by an empty option set — no choice ticked
 * is every choice offered — so its row is translated on the way in and out
 * (`offeredOptionIds`) and never passed to `boundsAreSet`, `valueMeetsBounds` or
 * `formatBoundsOnAxis`.
 */
export type AxisBounds = {
  /** The lower end, inclusive. Null is open at that end. NUMBER only. */
  minValue: number | null
  /** The upper end, inclusive. Null is open at that end. NUMBER only. */
  maxValue: number | null
  /** The answer the span is pinned to. BOOLEAN only. */
  boolValue: boolean | null
  /** One id is an equals, several are an "any of", none names no choice. */
  optionIds: number[]
}

/** What one row of a measure's requirement matrix asks on one axis. */
export type PropertyRequirement = AxisBounds & { definitionId: number }

/**
 * A span as a stored row carries it: three columns and the choices beside it.
 *
 * Three tables hold one — a measure's datasheet, a matrix condition, and
 * whatever the next one is — and each read spelled the same five-field unpack
 * out again. `boundsFromRow` is the one place a fifth column has to be added.
 */
export type BoundsRow = {
  minValue: number | null
  maxValue: number | null
  boolValue: boolean | null
  options: ReadonlyArray<{ optionId: number }>
}

export function boundsFromRow(row: BoundsRow): AxisBounds {
  return {
    minValue: row.minValue,
    maxValue: row.maxValue,
    boolValue: row.boolValue,
    optionIds: row.options.map(one => one.optionId),
  }
}

/** The three columns a span is stored in, for a write that states one. */
export function boundsColumns(
  bounds: AxisBounds,
): Pick<BoundsRow, 'minValue' | 'maxValue' | 'boolValue'> {
  return {
    minValue: bounds.minValue,
    maxValue: bounds.maxValue,
    boolValue: bounds.boolValue,
  }
}

/**
 * One row of that matrix: the conditions that have to hold together.
 *
 * A row that asks nothing at all — no condition and no price span — is met by
 * every product, which is the same thing a measure with no rows at all says.
 */
export type RequirementVariant = {
  conditions: readonly PropertyRequirement[]
  /** Net per the measure's own unit, both ends inclusive. Null is open. */
  priceMin: number | null
  priceMax: number | null
}

/** Whether the row bounds what a product may cost. Read by the matcher and by
 *  the counts, which load a price only where one does. */
export function variantBoundsPrice(variant: RequirementVariant): boolean {
  return variant.priceMin !== null || variant.priceMax !== null
}

/** Whether the row narrows anything. A row that does not takes every product
 *  of the measure's groups, which is rarely what adding one meant. */
export function variantAsksAnything(variant: RequirementVariant): boolean {
  return variant.conditions.length > 0 || variantBoundsPrice(variant)
}

/**
 * The conditions of one row that still bind, given the axes the measure's
 * groups describe their products by.
 *
 * Detaching a group leaves the conditions standing, so nothing is lost if the
 * group comes back — but the measure's screens render only the axes of the
 * current groups, so a condition on any other one is a filter nobody can see or
 * lift. Every reader of a stored row goes through here, or the matcher and the
 * screen disagree about what a row asks: one would call it open and the other
 * would show it reaching nothing.
 */
export function bindingConditions(
  conditions: ReadonlyArray<
    BoundsRow & {
      definitionId: number
      definition: { groups: ReadonlyArray<{ groupId: number }> }
    }
  >,
  /** The groups the measure is carried out with. */
  groupIds: ReadonlySet<number>,
): PropertyRequirement[] {
  return conditions
    .filter(condition =>
      condition.definition.groups.some(one => groupIds.has(one.groupId)),
    )
    .map(condition => ({
      definitionId: condition.definitionId,
      ...boundsFromRow(condition),
    }))
    .filter(boundsAreSet)
}

/**
 * The empty value of an axis: every column null.
 *
 * Beside `valueIsSet`, which is what reads it back: a fourth column added to
 * `PropertyValue` has to be cleared wherever a value is built from nothing —
 * the dialog that clears a field, the endpoint that stores one, the import
 * that reads one off a page.
 */
export function emptyPropertyValue(definitionId: number): PropertyValue {
  return {
    definitionId,
    numberValue: null,
    optionId: null,
    boolValue: null,
  }
}

/** A span that says nothing. Beside `emptyPropertyValue`, and for the same
 *  reason: a fifth column has to be cleared wherever one is built from
 *  nothing. */
export function emptyBounds(): AxisBounds {
  return {
    minValue: null,
    maxValue: null,
    boolValue: null,
    optionIds: [],
  }
}

/**
 * A span as a payload states it: a column it leaves out says nothing.
 *
 * Every surface that writes one goes through here — a measure's datasheet, a
 * matrix cell, either of them over MCP — so "absent" and "null" mean one thing
 * and a repeated choice cannot reach a table whose key is the pair.
 */
export function boundsFromPayload(
  payload: Partial<AxisBounds> | undefined,
): AxisBounds {
  return {
    minValue: payload?.minValue ?? null,
    maxValue: payload?.maxValue ?? null,
    boolValue: payload?.boolValue ?? null,
    optionIds: [...new Set(payload?.optionIds ?? [])],
  }
}

/** Whether a value says anything at all. */
export function valueIsSet(value: PropertyValue | undefined | null): boolean {
  if (!value) return false
  return (
    value.numberValue !== null ||
    value.optionId !== null ||
    value.boolValue !== null
  )
}

/**
 * Whether a span says anything at all.
 *
 * Most axes of most surfaces answer no: a measure prints a figure on two of
 * its axes and a matrix row narrows one or two, and the rest are open.
 */
export function boundsAreSet(bounds: AxisBounds): boolean {
  return (
    bounds.minValue !== null ||
    bounds.maxValue !== null ||
    bounds.boolValue !== null ||
    bounds.optionIds.length > 0
  )
}

/**
 * Whether a span fits the axis it is stated on.
 *
 * The mirror of `propertyValueError`, and for the same reason: the column that
 * is stated is the column `valueMeetsBounds` reads, so a bound on the wrong one
 * is not a harmless stray — it is a condition every product fails, and a
 * measure whose suggestion list silently empties looks exactly like a measure
 * nothing fits. `RANGE_MIN_ABOVE_MAX` is that same emptiness stated the other
 * way round.
 *
 * Nothing stated is allowed: an axis a surface says nothing about is the
 * ordinary case.
 */
export function boundsError(
  bounds: AxisBounds,
  property: EffectiveProperty,
): PropertyValueError | 'RANGE_MIN_ABOVE_MAX' | null {
  if (!boundsAreSet(bounds)) return null

  const { minValue, maxValue, boolValue, optionIds } = bounds

  if (property.type === 'NUMBER') {
    if (boolValue !== null || optionIds.length > 0) return 'WRONG_TYPE'
    // Each end against the axis' own range, as a single value always was: a
    // figure outside it is a datasheet line that contradicts the axis and a
    // condition no product of the group can answer.
    for (const end of [minValue, maxValue]) {
      if (end === null) continue
      if (property.minValue !== null && end < property.minValue) {
        return 'BELOW_MINIMUM'
      }
      if (property.maxValue !== null && end > property.maxValue) {
        return 'ABOVE_MAXIMUM'
      }
    }
    if (minValue !== null && maxValue !== null) {
      return minValue > maxValue ? 'RANGE_MIN_ABOVE_MAX' : null
    }
    return null
  }

  if (property.type === 'SELECT') {
    if (minValue !== null || maxValue !== null || boolValue !== null) {
      return 'WRONG_TYPE'
    }
    return optionIds.every(id =>
      property.options.some(option => option.id === id),
    )
      ? null
      : 'UNKNOWN_OPTION'
  }

  return minValue !== null || maxValue !== null || optionIds.length > 0
    ? 'WRONG_TYPE'
    : null
}

/**
 * Whether one value lies in one span.
 *
 * Read off the span rather than off the axis' type: the column that is stated
 * is the column that decides, and a span that states none is satisfied by
 * anything — including by a product that says nothing on that axis.
 *
 * A stated bound a product has no value for is **not** met. A product whose
 * datasheet is silent about a property the measure insists on is a product
 * nobody has checked, not a product that passes.
 */
export function valueMeetsBounds(
  value: PropertyValue | undefined | null,
  bounds: AxisBounds,
): boolean {
  if (!boundsAreSet(bounds)) return true
  if (!value) return false

  if (bounds.optionIds.length > 0) {
    return value.optionId !== null && bounds.optionIds.includes(value.optionId)
  }

  if (bounds.boolValue !== null) return value.boolValue === bounds.boolValue

  const number = value.numberValue
  if (number === null) return false
  if (bounds.minValue !== null && number < bounds.minValue) return false
  if (bounds.maxValue !== null && number > bounds.maxValue) return false
  return true
}

/**
 * Whether every condition of one matrix row is met.
 *
 * Called once per candidate pair by the matcher — a pass over one group weighs
 * measures × products × rows — so it answers on the first condition that fails
 * and builds nothing at all where a row states none.
 */
export function matchesRequirements(
  /** A product's values by axis. Handed in rather than built here: the matcher
   *  asks this once per candidate pair, over the same list each time. */
  values: ReadonlyMap<number, PropertyValue>,
  requirements: readonly PropertyRequirement[],
): boolean {
  if (requirements.length === 0) return true
  return requirements.every(requirement =>
    valueMeetsBounds(values.get(requirement.definitionId), requirement),
  )
}

/**
 * Whether one product meets one row: its datasheet and its price together.
 *
 * An unanswerable price is not a match: a product quoted in a unit the
 * measure's does not convert to is one nobody has priced for this measure,
 * and a row that bounds the price is asking exactly that question.
 */
export function matchesVariant(
  values: ReadonlyMap<number, PropertyValue>,
  variant: RequirementVariant,
  /** The product's price in the measure's own unit — `convertUnitPrice` —
   *  or null where the two units do not convert. Stück against m² is a
   *  question nobody can answer from what is stored. */
  price: number | null,
): boolean {
  if (variantBoundsPrice(variant)) {
    const { priceMin, priceMax } = variant
    if (price === null) return false
    if (priceMin !== null && price < priceMin) return false
    if (priceMax !== null && price > priceMax) return false
  }
  return matchesRequirements(values, variant.conditions)
}

/**
 * Whether a product meets what a measure asks: any one row, whole.
 *
 * The rows are an OR of ANDs, which is what lets one measure accept 4–6 mm
 * glued *or* 10–14 mm clicked without also accepting 12 mm glued. A measure
 * with no rows asks nothing and takes every product of its groups — the same
 * answer a single empty row gives, so neither state is a special case.
 */
export function matchesAnyVariant(
  values: ReadonlyMap<number, PropertyValue>,
  variants: readonly RequirementVariant[],
  price: number | null,
): boolean {
  if (variants.length === 0) return true
  return variants.some(variant => matchesVariant(values, variant, price))
}

/** A product's values by the axis they sit on, for the matcher to ask against
 *  many measures. */
export function valuesByAxis(
  values: readonly PropertyValue[],
): Map<number, PropertyValue> {
  return new Map(values.map(value => [value.definitionId, value]))
}

/**
 * What a card prints for one axis: a figure in its own unit, or a finished
 * line.
 *
 * Named here because three functions answer in it — a product's value, a
 * measure's span, and the free-text datasheet the two replace — and a card
 * that had to tell them apart would be a card that knows where each came from.
 */
export type DisplayValue = {
  type: 'NUMBER' | 'TEXT'
  textValue: string | null
  numberValue: number | null
  unit: Unit | null
  decimalPlaces?: number
}

/**
 * An axis, as much of it as printing one value needs.
 *
 * The choices are `{ id, label }` rather than whole options: the configurator
 * reads them off a table of its own, and asking for a key and a sort order it
 * does not carry would make it build rows to throw away.
 */
type LabelledAxis = Pick<PropertyDefinition, 'type' | 'unit'> & {
  decimalPlaces?: number
  options: readonly { id: number; label: string }[]
}

/**
 * A value on an axis, in the shape `formatPropertyValue` already renders.
 *
 * One conversion rather than a second renderer: the free-text datasheet this
 * replaces is drawn by that function — with the unit appended and the figure
 * written the German way — and a card that printed "14" beside a screen that
 * printed "14 mm" would be describing one product two ways. A choice and a
 * yes/no become the word a reader sees; the caller supplies those two words,
 * because `shared/utils` holds no German.
 */
function asDisplayValue(
  /** The value with its option already named — the product resolves it from
   *  the axis' choices, the configurator tree gets it from the join. */
  value: {
    numberValue?: number | null
    boolValue?: boolean | null
    optionLabel?: string | null
  } | null,
  /** `decimalPlaces` where the axis is known; omitted for one the list no
   *  longer holds, which prints the figure as it stands. */
  property: Pick<PropertyDefinition, 'type' | 'unit'> & {
    decimalPlaces?: number
  },
  words: { yes: string; no: string },
): DisplayValue {
  if (property.type === 'NUMBER') {
    return {
      type: 'NUMBER',
      textValue: null,
      numberValue: value?.numberValue ?? null,
      unit: property.unit,
      // The axis says how many places it is read to, and its input renders
      // exactly that; a card printing the raw figure beside it said 14,567
      // where the field said 14.
      decimalPlaces: property.decimalPlaces,
    }
  }

  const yesNo =
    value?.boolValue == null ? null : value.boolValue ? words.yes : words.no

  return {
    type: 'TEXT',
    textValue: value?.optionLabel ?? yesNo,
    numberValue: null,
    unit: null,
  }
}

/**
 * A value on an axis, as a reader sees it.
 *
 * Every surface that prints one goes through here, so a card, a datasheet and a
 * PDF describe one product the same way. The two words come from the caller,
 * because `shared/utils` holds no German.
 */
export function formatValueOnAxis(
  value: PropertyValue | undefined | null,
  property: LabelledAxis,
  words: { yes: string; no: string },
): string | null {
  const optionLabel =
    value?.optionId == null
      ? null
      : (property.options.find(one => one.id === value.optionId)?.label ?? null)
  return formatPropertyValue(
    asDisplayValue({ ...value, optionLabel }, property, words),
  )
}

/**
 * A measure's span on one axis, as its datasheet prints it.
 *
 * Through `boundsAsDisplayValue`, which is also what the configurator card
 * draws, so the two cannot describe one axis differently: a lone figure is that
 * figure on both — never "ab 14 mm" on one and "14 mm" on the other — and
 * anything a figure cannot say is the line below.
 *
 * Beside `formatValueOnAxis`, which does the same for a product's single value.
 */
export function formatSpanOnAxis(
  bounds: AxisBounds,
  property: LabelledAxis,
  words: BoundsWords,
): string | null {
  return formatPropertyValue(boundsAsDisplayValue(bounds, property, words))
}

/** The words a span is read out with, beyond the axis' own. */
export type BoundsWords = {
  yes: string
  no: string
  from: string
  to: string
}

/**
 * A span between two numbers, as the line a bound on them is read out as.
 *
 * The one shape every bounded cell of the matrix draws: the figures bare, the
 * unit said once at the end, an unspaced en-dash between them, and an open
 * end read as a bound — "ab 14 mm", "bis 50 €/m²". Two surfaces state a span
 * side by side in one table row, an axis and a price, and written out twice
 * they disagreed about the dash and about the rounding.
 *
 * `figure` writes one number and `withUnit` finishes the line, because that
 * is all the two differ by.
 */
export function formatSpan(
  min: number | null,
  max: number | null,
  parts: {
    figure: (value: number) => string
    withUnit: (text: string) => string
  },
  words: Pick<BoundsWords, 'from' | 'to'>,
): string | null {
  const { figure, withUnit } = parts
  if (min !== null && max !== null) {
    return withUnit(`${figure(min)}–${figure(max)}`)
  }
  if (min !== null) return `${words.from} ${withUnit(figure(min))}`
  if (max !== null) return `${words.to} ${withUnit(figure(max))}`
  return null
}

/**
 * A span on one axis, as the line a bound is read out as.
 *
 * An open end is a bound here — "ab 14 mm" is what a matrix condition means by
 * one — which is why a datasheet goes through `formatSpanOnAxis` instead: there
 * a lone figure is the figure itself. Null where the span says nothing, which is
 * the ordinary case.
 */
export function formatBoundsOnAxis(
  bounds: AxisBounds,
  property: LabelledAxis,
  words: BoundsWords,
): string | null {
  if (!boundsAreSet(bounds)) return null

  const figure = (value: number) =>
    formatNumber(value, {
      minimumFractionDigits: property.decimalPlaces ?? 0,
      maximumFractionDigits: property.decimalPlaces ?? 0,
    })
  const symbol = displayUnitSymbol(property.unit)
  const withUnit = (text: string) => (symbol ? `${text} ${symbol}` : text)

  if (bounds.optionIds.length > 0) {
    // Null where none of them is a choice this axis still carries: an empty
    // string renders as a blank cell that a reader takes for "says nothing",
    // while `boundsAreSet` calls the same span stated.
    const named = property.options
      .filter(option => bounds.optionIds.includes(option.id))
      .map(option => option.label)
    return named.length > 0 ? named.join(', ') : null
  }

  if (bounds.boolValue !== null) {
    return bounds.boolValue ? words.yes : words.no
  }

  return formatSpan(
    bounds.minValue,
    bounds.maxValue,
    { figure, withUnit },
    words,
  )
}

/**
 * A measure's span on one axis, as a card's two columns.
 *
 * A lone figure stays a figure, so a card keeps the unit and the decimal count
 * it formats every number with. Anything a span cannot say as one number — a
 * range, several choices, a yes/no — is the finished line above, because the
 * card has one text column and no notion of a span.
 */
export function boundsAsDisplayValue(
  bounds: AxisBounds,
  property: LabelledAxis,
  words: BoundsWords,
): DisplayValue {
  if (property.type === 'NUMBER' && bounds.maxValue === null) {
    return asDisplayValue({ numberValue: bounds.minValue }, property, words)
  }

  return {
    type: 'TEXT',
    textValue: formatBoundsOnAxis(bounds, property, words),
    numberValue: null,
    unit: null,
  }
}

/** Why a value cannot be stored, as a code the caller turns into a message. */
export type PropertyValueError =
  | 'WRONG_TYPE'
  | 'BELOW_MINIMUM'
  | 'ABOVE_MAXIMUM'
  | 'UNKNOWN_OPTION'

/**
 * Whether a value fits the axis it is being written to, as the writing group
 * presents it.
 *
 * An empty value is always allowed: `isRequired` marks a field as expected and
 * an import records a missing one as a gap, but nothing refuses a datasheet for
 * being incomplete.
 */
export function propertyValueError(
  value: PropertyValue,
  property: EffectiveProperty,
): PropertyValueError | null {
  if (!valueIsSet(value)) return null

  if (property.type === 'NUMBER') {
    if (value.numberValue === null) return 'WRONG_TYPE'
    if (property.minValue !== null && value.numberValue < property.minValue) {
      return 'BELOW_MINIMUM'
    }
    if (property.maxValue !== null && value.numberValue > property.maxValue) {
      return 'ABOVE_MAXIMUM'
    }
    return null
  }

  if (property.type === 'SELECT') {
    if (value.optionId === null) return 'WRONG_TYPE'
    return property.options.some(option => option.id === value.optionId)
      ? null
      : 'UNKNOWN_OPTION'
  }

  return value.boolValue === null ? 'WRONG_TYPE' : null
}
